import { useEffect, useRef } from "react";

import {
  getPlanningOperationMessage,
  PLANNING_SYNC_ENABLED,
  PLANNING_SYNC_EVENTS_LIMIT,
  PLANNING_SYNC_POLL_MS,
  shouldNotifyPlanningOperation,
  shouldSyncPlanningOperation,
} from "../../../config";
import { listPlanningSyncEvents } from "../../../services/api/planningSync";

function getEventCursor(event) {
  return event?.created_at ?? event?.createdAt ?? null;
}

function getEventClientInstanceId(event) {
  return event?.metadata?.client_instance_id ?? event?.metadata?.clientInstanceId ?? null;
}

/**
 * Poll the planning sync-log and route remote model updates to module reload handlers.
 *
 * The hook generalizes the working Dispatch poller pattern. Callers provide a
 * resource-to-reload map so the same polling behavior can refresh Dispatch,
 * Schedule, or future planning modules without duplicating timer/cursor logic.
 *
 * @param {Object} params
 * @param {string} params.currentModule The module using this poller ("schedule" or "dispatch").
 * @param {string} [params.clientInstanceId] Current browser-tab id; matching events are ignored.
 * @param {(message: string, type?: string) => void} [params.notify]
 * @param {(event: Object) => void} [params.onLatestEvent] Receives the newest remote sync row.
 * @param {Record<string, () => void|Promise<void>>} params.reloadByResource
 */
export function usePlanningSyncPoller({
  currentModule,
  clientInstanceId,
  notify,
  onLatestEvent,
  reloadByResource,
}) {
  const lastSeenSyncCursorRef = useRef(new Date().toISOString());

  useEffect(() => {
    if (!PLANNING_SYNC_ENABLED) {
      return undefined;
    }

    /**
     * Pull newer sync-log rows, reload each affected resource at most once,
     * then surface source-aware messages for events created by other clients.
     */
    const pullSyncEvents = async () => {
      try {
        const response = await listPlanningSyncEvents({
          since: lastSeenSyncCursorRef.current,
          limit: PLANNING_SYNC_EVENTS_LIMIT,
        });
        const events = Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
            ? response.results
            : [];
        const remoteEvents = events.filter(
          (event) => getEventClientInstanceId(event) !== clientInstanceId
        );
        const newestRemoteEvent = remoteEvents[remoteEvents.length - 1];
        if (newestRemoteEvent) {
          onLatestEvent?.(newestRemoteEvent);
        }

        const reloadResources = new Set();
        for (const event of remoteEvents) {
          const operation = event?.operation;
          if (!shouldSyncPlanningOperation(operation, currentModule)) {
            continue;
          }
          const resources = Array.isArray(event?.resources) ? event.resources : [];
          for (const resource of resources) {
            if (typeof reloadByResource?.[resource] === "function") {
              reloadResources.add(resource);
            }
          }
        }

        for (const resource of reloadResources) {
          await reloadByResource[resource]();
        }

        for (const event of remoteEvents) {
          const operation = event?.operation;
          if (!shouldNotifyPlanningOperation(operation, currentModule)) {
            continue;
          }
          const resources = Array.isArray(event?.resources) ? event.resources : [];
          const affectsThisModule = resources.some(
            (resource) => typeof reloadByResource?.[resource] === "function"
          );
          const syncsThisModule = shouldSyncPlanningOperation(operation, currentModule);
          if (!affectsThisModule && syncsThisModule) {
            continue;
          }
          const actorName = event?.actor_name ?? event?.actorName ?? "Unknown user";
          const message =
            getPlanningOperationMessage(operation, event?.source_module ?? currentModule) ??
            event?.notification_message ??
            event?.notificationMessage;
          notify?.(
            `${String(event?.source_module ?? "Planning")
              .replace(/^./, (char) => char.toUpperCase())}: ${actorName} ${message}`,
            "info"
          );
        }

        const newestEvent = events[events.length - 1];
        const newestCursor = response?.cursor ?? getEventCursor(newestEvent);
        if (newestCursor) {
          lastSeenSyncCursorRef.current = newestCursor;
        }
      } catch (syncErr) {
        console.error(`${currentModule} planning sync poll failed`, syncErr);
      }
    };

    const timer = setInterval(() => {
      void pullSyncEvents();
    }, PLANNING_SYNC_POLL_MS);

    return () => clearInterval(timer);
  }, [clientInstanceId, currentModule, notify, onLatestEvent, reloadByResource]);
}
